import React, { useEffect, useState } from 'react';
import { useNewConsentModalContext } from 'pages/SharePage/Context/NewConsentModalContext';
import { BrInput, BrButton } from '@govbr-ds/react-components';
import { Box } from '@material-ui/core';
import { toast } from 'react-toastify';
import { elasticRequests } from 'apiServices/elasticSearchAPI/elasticRequests';
import { connectorRequests } from 'apiServices/connectorMonitorAPI/connectorMonitor';
import { GovBrDateInput, GovBrSelect } from 'components';
import { LoadingHU } from 'components/LoadingHU';
import MHDRequests from 'services/MHDRequests';
import { oids } from 'configs';

function cleanConnectorResponse(data) {
  const dictTypeDocuments = {
    PatientData: 'Cadastro de paciente',
    RACData: 'Registro de atendimento clínico',
    SAData: 'Sumário de alta',
    AttachmentModel: 'Documento assinado (atestados, prescrições, etc.)',
    ResultadoExameData: 'Resultado de exame',
  };

  const responseItem = data
    ?.map((el) => {
      const populateActions = el?.action
        ? el.action?.map((act) => {
            const value = {
              patientId: act?.parameters?.patient || act?.parameters?.patientId,
              documentId:
                act?.parameters?.identifier || act?.parameters?.localId,
              documentType: dictTypeDocuments[act?.parameters?.model] || '-',
              organization: el.organization,
              status: el.status,
              startDate: el?.created || '-',
              endDate: el?.updated || '-',
            };
            return value;
          })
        : {
            patientId: '-',
            documentId: '-',
            documentType: '-',
            organization: el.organization,
            status: el.status,
            startDate: el?.created || '-',
            endDate: el?.updated || '-',
          };
      return populateActions;
    })
    ?.filter((e) => e !== undefined);

  const lines = [].concat([], ...responseItem);
  return lines;
}

// eslint-disable-next-line react/prop-types
export function SearchFields({
  setSearch,
  setData,
  setElasticFilter,
  setConnnectorFilter,
  setConnectorResponse,
}) {
  const { organizationList } = useNewConsentModalContext();

  const [identifier, setIdentifier] = useState('');
  const [healthLocale, setHealthLocale] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [render, setRender] = useState(true);
  const [isLoading, setIsLoading] = useState(false); // Estado para controlar o loading

  const [dataStart, setDataStart] = useState('');
  const [dataEnd, setDataEnd] = useState('');
  const [status, setStatus] = useState('');

  const arrayTypeDocuments = [
    'Cadastro de paciente',
    'Documento assinado (atestados, prescrições, etc.)',
    'Registro de atendimento clínico',
    'Resultado de exame',
    'Sumário de alta',
  ];

  const dictTypeStatus = {
    'Falha de conexão': 'NETWORK_ERROR',
    'Dados do paciente não conferem com o Cadastro Nacional de Saúde':
      'PATIENT_DEMOGRAPHICS_ERROR',
    'Identificação do paciente ausente ou inconsistente':
      'PATIENT_IDENTIFIER_ERROR',
    'Erro desconhecido no processamento': 'SERVER_ERROR',
    'Informação inválida no atendimento': 'VALIDATION_ERROR',
  };

  const dictTypeDocuments = {
    'Cadastro de paciente': 'PatientData',
    'Registro de atendimento clínico': 'RACData',
    'Sumário de alta': 'SAData',
    'Documento assinado (atestados, prescrições, etc.)': 'AttachmentModel',
    'Resultado de exame': 'ResultadoExameData',
  };

  const handleSearch = async () => {
    // Verificar se já está carregando antes de prosseguir
    if (isLoading) {
      return;
    }

    setIsLoading(true); // Inicia o loading

    let ids;

    try {
      if (identifier?.length == 11 || identifier?.length === 15) {
        const patientJson = await MHDRequests.getPatientJson({
          patientId: identifier,
        });

        const patientIdentifier = patientJson?.identifier;

        ids = patientIdentifier;
      }

      const cnesToSearch = organizationList?.find(
        (el) => el?.resource?.alias?.[0] === healthLocale,
      )?.resource?.identifier?.[0]?.value;
      const documentToSearch = dictTypeDocuments[`${documentType}`];
      const statusSearch = dictTypeStatus[`${status}`];

      // Processar datas no formato HH:mm-YYYY-MM-DD ou apenas YYYY-MM-DD
      let auxStartDate = '';
      let auxEndDate = '';
      let startTime = '';
      let endTime = '';

      // Extrair data e hora do formato HH:mm-YYYY-MM-DD ou apenas YYYY-MM-DD
      if (dataStart && dataStart.includes('-')) {
        const parts = dataStart.split('-');

        // Formato completo HH:mm-YYYY-MM-DD
        if (parts.length === 4) {
          const [time, year, month, day] = parts;
          auxStartDate = `${year}-${month}-${day}`;

          // Se o usuário digitou hora, use-a
          if (time && time !== '00:00') {
            startTime = `T${time}:00`;
          } else {
            // Se for data inicial sem hora definida, considere desde o início do dia
            startTime = 'T00:00:00';
          }
        }
        // Formato apenas com data YYYY-MM-DD (sem hora)
        else if (parts.length === 3) {
          const [year, month, day] = parts;
          auxStartDate = `${year}-${month}-${day}`;
          // Para data inicial sem hora definida, considere desde o início do dia
          startTime = 'T00:00:00';
        }
      } else if (dataStart) {
        // Formato antigo dd/mm/yyyy
        auxStartDate = dataStart.split('/')?.reverse().join('-');
        // Para formato antigo, considere desde o início do dia
        startTime = 'T00:00:00';
      }

      // Processar data final no mesmo formato
      if (dataEnd && dataEnd.includes('-')) {
        const parts = dataEnd.split('-');

        // Formato completo HH:mm-YYYY-MM-DD
        if (parts.length === 4) {
          const [time, year, month, day] = parts;
          auxEndDate = `${year}-${month}-${day}`;

          // Se o usuário digitou hora, use-a
          if (time && time !== '00:00') {
            endTime = `T${time}:00`;
          } else {
            // Se for data final sem hora definida, considere até o final do dia
            endTime = 'T23:59:59';
          }
        }
        // Formato apenas com data YYYY-MM-DD (sem hora)
        else if (parts.length === 3) {
          const [year, month, day] = parts;
          auxEndDate = `${year}-${month}-${day}`;
          // Para data final sem hora definida, considere até o final do dia
          endTime = 'T23:59:59';
        }
      } else if (dataEnd) {
        // Formato antigo dd/mm/yyyy
        auxEndDate = dataEnd.split('/')?.reverse().join('-');
        // Para formato antigo, considere até o final do dia
        endTime = 'T23:59:59';
      }

      // Construa os filtros garantindo que temos os valores T de tempo quando necessário
      const patientId = ids
        ?.map((el) => `patient_id:"${el?.value}"`)
        ?.join(' OR ');
      const auxFilter = [
        { filter: patientId && `(${patientId})` },
        { filter: cnesToSearch && `cnes:"${cnesToSearch}"` },
        { filter: documentToSearch && `model:"${documentToSearch}"` },
        {
          filter:
            (dataStart || dataEnd) &&
            `@timestamp:[${auxStartDate || '2000-01-01'}${startTime} TO ${
              auxEndDate || '2060-01-01'
            }${endTime}] `,
        },
      ]
        ?.filter((el) => el.filter)
        ?.map((e) => e.filter)
        ?.join(' AND ');

      const patient = ids?.find((element) =>
        element.system.includes(oids.cpf),
      )?.value;

      const auxParam = [
        { param: patient && `"patient":"${patient}"` },
        { param: documentToSearch && `"model":"${documentToSearch}"` },
      ]
        ?.filter((el) => el.param)
        ?.map((e) => e.param)
        ?.join(',');

      const connectorFilters = [
        {
          filter: cnesToSearch && `organization=${cnesToSearch}`,
        },
        {
          filter: dataStart && `start=${auxStartDate}`,
        },
        {
          filter: dataEnd && `end=${auxEndDate}`,
        },
        {
          filter: auxParam && `parameters={${auxParam}}`,
        },
      ]
        ?.filter((el) => el.filter)
        ?.map((e) => e.filter)
        ?.join('&');

      setConnnectorFilter(connectorFilters);
      setElasticFilter(auxFilter);

      if (identifier && identifier?.length !== 11 && identifier?.length !== 15)
        throw new Error('Identifier');
      if (identifier?.length && !ids) throw new Error('Not found');

      // Já não precisamos validar o formato de data, pois o componente GovBrDateInput
      // já garante que está no formato correto HH:mm-YYYY-MM-DD

      setSearch(true);
      const conector_monitor =
        await connectorRequests.getTasks(connectorFilters);

      setConnectorResponse(
        cleanConnectorResponse(conector_monitor?.['_items'] || []),
      );

      const patient_link = await elasticRequests.PatientLink({
        filter: auxFilter,
      });

      // Extrair os local_ids dos resultados do PatientLink
      const localIds = patient_link?.hits?.hits.map(
        (el) => el?._source?.local_id,
      );

      // Chamar a nova função ConnectorAuditPost com os parâmetros estruturados
      const connector_audit = await elasticRequests.ConnectorAuditPost({
        localIds,
        cnes: cnesToSearch,
        model: documentToSearch,
        status: statusSearch,
        startDate: dataStart && `${auxStartDate}${startTime}`,
        endDate: dataEnd && `${auxEndDate}${endTime}`,
      });

      const merge = patient_link?.hits?.hits?.map((el) => {
        const reference = connector_audit?.hits?.hits?.find(
          (e) =>
            e?._source?.instance === el?._source?.instance &&
            e?._source?.model === el?._source?.model &&
            e?._source?.local_id === el?._source?.local_id,
        );
        return {
          ...el._source,
          status: reference?._source?.status,
          cnes: reference?._source?.cnes || el._source?.cnes,
        };
      });

      setData(merge?.filter((el) => el.status));

      // Toast de sucesso só aparece depois que o loading para
      toast.success(`A busca foi realizada com sucesso`);
    } catch (err) {
      console.log('err', err);

      // As mensagens de erro só aparecem depois que o loading para
      if (err.toString().includes('Identifier')) {
        toast.warn('CPF ou CNS inválido');
      } else if (err.toString().includes('Not found')) {
        toast.warn('Paciente não encontrado');
      } else {
        toast.error(
          'Não foi possível finalizar a operação, verifique os filtros e tente novamente',
        );
      }
    } finally {
      setIsLoading(false); // Finaliza o loading independente do resultado
    }
  };

  const handleClear = () => {
    setIdentifier('');
    setHealthLocale('');
    setDocumentType('');
    setDataStart('');
    setDataEnd('');
    setStatus('');
    setSearch(false);
    setRender(false);
    setIsLoading(false); // Garante que o loading seja resetado ao limpar
  };

  useEffect(() => {
    if (!render) setRender(true);
  }, [render]);

  return (
    <>
      {render && (
        <>
          <div className="row">
            <div className="col-sm-2 mb-1">
              <BrInput
                style={{
                  border: '1px solid',
                  borderColor: 'var(--secondary-background-color)',
                }}
                value={identifier}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const numericRegex = /^[0-9]*$/;

                  if (
                    numericRegex.test(newValue) &&
                    e.target.value?.length < 16
                  ) {
                    setIdentifier(newValue);
                  }
                }}
                placeholder="CPF ou CNS do paciente"
                label="CPF ou CNS"
                type="datetime"
                disabled={isLoading} // Desabilita campos durante o loading
              />
            </div>
            <div className="col-sm-2 mb-1">
              <GovBrSelect
                value={healthLocale}
                setValue={setHealthLocale}
                list={organizationList?.map((el) => el?.resource?.alias?.[0])}
                label="Unidade hospitalar"
                disabled={isLoading} // Desabilita campos durante o loading
              />
            </div>

            <div className="col-sm-2 mb-1">
              <GovBrSelect
                value={documentType}
                setValue={setDocumentType}
                list={arrayTypeDocuments}
                label="Tipos de documentos"
                disabled={isLoading} // Desabilita campos durante o loading
              />
            </div>

            <GovBrDateInput
              value={dataStart}
              setValue={setDataStart}
              label="Data inicial"
              includeTime={true}
              disabled={isLoading} // Desabilita campos durante o loading
            />
            <GovBrDateInput
              value={dataEnd}
              setValue={setDataEnd}
              label="Data final"
              includeTime={true}
              disabled={isLoading} // Desabilita campos durante o loading
            />
            <div className="col mb-1">
              <GovBrSelect
                value={status}
                setValue={setStatus}
                list={Object.keys(dictTypeStatus)}
                label="Status"
                disabled={isLoading} // Desabilita campos durante o loading
              />
            </div>
          </div>

          {/* Componente de Loading */}
          <LoadingHU state={isLoading} />

          <Box display="flex" flexDirection="row-reverse" gridGap={20}>
            <BrButton
              primary
              sx={{ width: '10%' }}
              onClick={handleSearch}
              disabled={isLoading} // Só desabilita durante o loading
            >
              Pesquisar
            </BrButton>
            <BrButton
              secondary
              sx={{ width: '10%' }}
              onClick={handleClear}
              disabled={isLoading} // Desabilita limpar durante o loading
            >
              Limpar
            </BrButton>
          </Box>
        </>
      )}
    </>
  );
}
